// src/pages/ShopSettings.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useMerchant } from '../context/MerchantContext';
import { updateShop, merchantLogout } from '../services/firebase';

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export default function ShopSettings() {
  const navigate = useNavigate();
  const { shop, setShop } = useMerchant();
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    name: shop?.name || '',
    description: shop?.description || '',
    address: shop?.address || '',
    phone: shop?.phone || '',
    openTime: shop?.openTime || '08:00',
    closeTime: shop?.closeTime || '21:00',
    closedDays: shop?.closedDays || [],
    minOrder: shop?.minOrder || 0,
    deliveryTime: shop?.deliveryTime || '30-45',
    active: shop?.active !== false,
    accountName: shop?.accountName || '',
    bankName: shop?.bankName || '',
    accountNumber: shop?.accountNumber || '',
    ifsc: shop?.ifsc || '',
    upiId: shop?.upiId || '',
  });

  const toggleDay = (day) => {
    setForm(f => ({
      ...f,
      closedDays: f.closedDays.includes(day)
        ? f.closedDays.filter(d => d !== day)
        : [...f.closedDays, day],
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Enter shop name'); return; }
    setSaving(true);
    try {
      await updateShop(shop.id, form);
      setShop({ ...shop, ...form });
      toast.success('Shop updated! ✅');
    } catch (e) {
      toast.error('Could not save changes');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--black)', maxWidth: 900, margin: '0 auto' }}>

      {/* Topbar */}
      <div style={{ background: 'var(--dark)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', alignItems: 'center', height: 60, gap: 16 }}>
        <div style={{ fontSize: 20 }}>🏪</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--green)' }}>{shop?.name}</div>
          <div style={{ fontSize: 11, color: 'var(--gray)' }}>Merchant Dashboard</div>
        </div>
        {[
          { label: '📦 Orders', path: '/' },
          { label: '💰 Earnings', path: '/earnings' },
          { label: '🛒 Products', path: '/products' },
          { label: '⚙️ Settings', path: '/settings', active: true },
        ].map(n => (
          <button key={n.label} onClick={() => navigate(n.path)} style={{
            padding: '7px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
            background: n.active ? 'rgba(0,214,143,0.1)' : 'transparent',
            color: n.active ? 'var(--green)' : 'var(--gray)',
            fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 700,
          }}>{n.label}</button>
        ))}
        <button onClick={() => merchantLogout()} style={{ background: 'none', border: 'none', color: 'var(--gray)', cursor: 'pointer', fontSize: 13 }}>Logout</button>
      </div>

      <div style={{ padding: 24, maxWidth: 600 }}>
        <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 24 }}>Shop Settings ⚙️</div>

        {/* Shop active toggle */}
        <div className="card" style={{ padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800 }}>Shop Status</div>
            <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 2 }}>{form.active ? 'Customers can place orders' : 'Shop is closed, no new orders'}</div>
          </div>
          <div onClick={() => setForm(f => ({ ...f, active: !f.active }))} style={{ width: 52, height: 28, borderRadius: 14, background: form.active ? 'var(--green)' : 'var(--border)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
            <div style={{ width: 24, height: 24, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: form.active ? 26 : 2, transition: 'left 0.2s' }} />
          </div>
        </div>

        {/* Basic info */}
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--gray)', marginBottom: 16 }}>BASIC INFO</div>
          {[
            { label: 'SHOP NAME', key: 'name', placeholder: 'Sri Balaji Supermarket' },
            { label: 'ADDRESS', key: 'address', placeholder: 'Shop no., Street, Piler' },
            { label: 'PHONE', key: 'phone', placeholder: '+91 9000000000' },
          ].map(f => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>{f.label}</div>
              <input placeholder={f.placeholder} value={form[f.key]} onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))} />
            </div>
          ))}
          <div style={{ marginBottom: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>DESCRIPTION</div>
            <textarea placeholder="Tell customers about your shop..." value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} style={{ height: 80, resize: 'none' }} />
          </div>
        </div>

        {/* Hours */}
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--gray)', marginBottom: 16 }}>OPENING HOURS</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>OPENS AT</div>
              <input type="time" value={form.openTime} onChange={e => setForm(f => ({ ...f, openTime: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>CLOSES AT</div>
              <input type="time" value={form.closeTime} onChange={e => setForm(f => ({ ...f, closeTime: e.target.value }))} />
            </div>
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 10 }}>CLOSED ON</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {DAYS.map(day => (
              <div key={day} onClick={() => toggleDay(day)} style={{
                padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 600,
                border: `1.5px solid ${form.closedDays.includes(day) ? 'var(--red)' : 'var(--border)'}`,
                background: form.closedDays.includes(day) ? 'rgba(239,68,68,0.1)' : 'var(--dark)',
                color: form.closedDays.includes(day) ? 'var(--red)' : 'var(--gray)',
              }}>{day.slice(0, 3)}</div>
            ))}
          </div>
        </div>

        {/* Bank / UPI for payouts */}
        <div className="card" style={{ padding: 20, marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--gray)', marginBottom: 8 }}>PAYOUT DETAILS</div>
          <div style={{ fontSize: 12, color: 'var(--gray)', marginBottom: 16, lineHeight: 1.5 }}>
            Add your bank or UPI details so MyTownly admin can transfer daily earnings.
          </div>
          {[
            { label: 'ACCOUNT HOLDER NAME', key: 'accountName', placeholder: 'As per bank account' },
            { label: 'BANK NAME', key: 'bankName', placeholder: 'e.g. SBI, HDFC' },
            { label: 'ACCOUNT NUMBER', key: 'accountNumber', placeholder: 'Bank account number' },
            { label: 'IFSC CODE', key: 'ifsc', placeholder: 'e.g. SBIN0001234' },
            { label: 'UPI ID', key: 'upiId', placeholder: 'e.g. name@upi' },
          ].map((f) => (
            <div key={f.key} style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>{f.label}</div>
              <input
                placeholder={f.placeholder}
                value={form[f.key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
              />
            </div>
          ))}
        </div>

        {/* Delivery settings */}
        <div className="card" style={{ padding: 20, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: 'var(--gray)', marginBottom: 16 }}>DELIVERY SETTINGS</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>MIN ORDER (₹)</div>
              <input type="number" placeholder="0" value={form.minOrder} onChange={e => setForm(f => ({ ...f, minOrder: e.target.value }))} />
            </div>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray)', marginBottom: 6 }}>DELIVERY TIME</div>
              <select value={form.deliveryTime} onChange={e => setForm(f => ({ ...f, deliveryTime: e.target.value }))} style={{ width: '100%', background: 'var(--card)', border: '1.5px solid var(--border)', borderRadius: 12, padding: '12px 14px', color: 'var(--text)', fontFamily: 'DM Sans, sans-serif', fontSize: 14, outline: 'none' }}>
                {['15-20 mins', '20-30 mins', '30-45 mins', '45-60 mins', '1-2 hours'].map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          </div>
        </div>

        <button className="btn btn-green" onClick={handleSave} disabled={saving}>
          {saving ? <span className="spinner" /> : 'Save Changes ✅'}
        </button>
      </div>
    </div>
  );
}
